import { type ElectronicTradingWidgetProps } from '@cian/frontend-electronic-trading-widget';
import { useToggleState } from '@cian/react-utils';
import { Button, ModalWindow } from '@cian/ui-kit';

import { type FC, useCallback, useMemo } from 'react';

import { useListingAppContext } from '@/microfrontends/listing/shared/context/ListingAppContext';
import {
  useCardOfferDataContext,
  useCardOfferPublisherContext,
} from '@/microfrontends/listing/shared/context/selectors/cardData';
import { useCAForCard } from '@/microfrontends/listing/shared/hooks/card/useCAForCard';
import { formatPhone } from '@/microfrontends/listing/shared/utils/formatPhone';

import { extractUndefined } from '@/packages/utils/extractUndefined';

import { ElectronicTradingWidgetLoadableFallback } from '../components/ElectronicTradingWidgetLoadableFallback';

import { ElectronicTradingWidgetLoadable } from './ElectronicTradingWidgetLoadable';

type JoinElectronicTradingButtonContainerProps = {
  buttonText: string;
};

const MODAL_WIDTHS = {
  narrow: 498,
  wide: 680,
};

export const CardJoinElectronicTradingButtonContainer: FC<JoinElectronicTradingButtonContainerProps> = props => {
  const { buttonText } = props;
  const { config } = useListingAppContext();
  const { tradingLink } = useCardOfferDataContext();
  const { phones, media, typeLabels, name } = useCardOfferPublisherContext();
  const caForCard = useCAForCard();
  const { state: isModalVisible, toggle: toggleModalVisibility } = useToggleState(false);

  const legalLink = config.getStrict<string>('electronicTrading.legalDocsUrl');

  const trackJoinTrade = useCallback((): void => {
    caForCard({ action: 'Phones_Rosimuschestvo_popup_click', category: 'Listing' });
  }, [caForCard]);

  const trackShowTradingWidgetModal = useCallback((): void => {
    caForCard({ action: 'Open_listing_rosimuschestvo', category: 'Phones' }, { label: true });
  }, [caForCard]);

  /**
   * Эти события трекать не требуется. Добавить, если требования поменяются.
   */
  const trackLegalLinkClick = useCallback((): void => {}, []);
  const trackPublisherPhoneNumberClick = useCallback((): void => {}, []);

  const widgetProps = useMemo((): ElectronicTradingWidgetProps => {
    const prettyPhones = phones.map(formatPhone);
    const publisherTypeLabel = typeLabels?.[0] || '';

    return {
      deviceType: 'desktop',
      joinTradeButtonLink: tradingLink,
      legalLink,
      publisherAvatarSrc: extractUndefined(media.picture),
      publisherName: name,
      publisherPhoneNumbers: prettyPhones,
      publisherTypeLabel,
      trackJoinTrade,
      trackLegalLinkClick,
      trackPublisherPhoneNumberClick,
    };
  }, [
    legalLink,
    media.picture,
    name,
    typeLabels,
    phones,
    trackJoinTrade,
    trackLegalLinkClick,
    trackPublisherPhoneNumberClick,
    tradingLink,
  ]);
  const handleClick = useCallback(() => {
    toggleModalVisibility();
    trackShowTradingWidgetModal();
  }, [toggleModalVisibility, trackShowTradingWidgetModal]);

  return (
    <>
      {isModalVisible && (
        <ModalWindow
          fixed
          open
          portal
          content={
            <ElectronicTradingWidgetLoadable fallback={<ElectronicTradingWidgetLoadableFallback />}>
              {(Lib): JSX.Element => <Lib.ElectronicTradingWidget {...widgetProps} />}
            </ElectronicTradingWidgetLoadable>
          }
          size="M"
          title="Переходим к электронным торгам"
          width={widgetProps.publisherPhoneNumbers.length ? MODAL_WIDTHS.wide : MODAL_WIDTHS.narrow}
          onClose={toggleModalVisibility}
        />
      )}
      <Button
        fullWidth
        data-testid="electronic-trading.join-trading"
        size="XS"
        theme="fill_primary"
        onClick={handleClick}
      >
        {buttonText}
      </Button>
    </>
  );
};
